import { useState, useEffect } from 'react';
import { fmtClock } from '../lib/format.js';

export function EditableTime({ value, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  const [saving, setSaving] = useState(false);
  // Only sync the local draft from the value prop when we're not
  // mid-edit AND not mid-save. CJ feedback 2026-06-30: managers were
  // typing a new time, clicking out, and seeing the OLD time until they
  // clicked back in — caused by the span re-rendering with the stale
  // `value` prop before the parent's PUT + refetch completed. Holding
  // `v` through the save window lets the span show what the manager
  // just typed, with the canonical server value taking over once it
  // lands.
  useEffect(() => {
    if (!editing && !saving) setV(value || '');
  }, [value, editing, saving]);

  async function commit() {
    setEditing(false);
    if ((v || '') === (value || '')) return;
    setSaving(true);
    try { await onSave(v); }
    finally { setSaving(false); }
  }

  if (!editing) {
    // While saving, show the typed value (optimistic) plus a thin
    // ellipsis so the manager knows the write is in flight.
    const displayed = saving ? v : value;
    return (
      <span onClick={() => !disabled && !saving && setEditing(true)}
        style={{ cursor: (disabled || saving) ? 'default' : 'pointer',
                  color: displayed ? '#212121' : '#bdbdbd',
                  borderBottom: disabled ? 'none' : '1px dashed #d0d4d9',
                  paddingBottom: 1,
                  opacity: saving ? 0.7 : 1 }}>
        {displayed ? fmtClock(displayed) : '—'}{saving ? '…' : ''}
      </span>
    );
  }
  // <input type="time"> always uses the user's locale for display, but
  // the raw value the input emits is 24h HH:MM — that's what we send to
  // the server and what the schedule pipeline expects. Display side
  // only converts to 12h.
  return (
    <input autoFocus type="time" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setEditing(false); setV(value || ''); }
      }}
      style={{ padding: '2px 4px', fontSize: 12, border: '1px solid var(--forest)', borderRadius: 4, fontFamily: 'inherit' }} />
  );
}
